import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Recipient } from '@/lib/types/recipients';

interface RecipientsUIState {
  // Sidepanel (view details)
  selectedRecipient: Recipient | null;
  sidepanelOpen: boolean;
  openSidepanel: (recipient: Recipient) => void;
  closeSidepanel: () => void;
  updateSelectedRecipient: (updates: Record<string, unknown>) => void;

  // Edit sidepanel
  editRecipient: Recipient | null;
  editSidepanelOpen: boolean;
  openEditSidepanel: (recipient: Recipient) => void;
  closeEditSidepanel: () => void;

  // Add modal/sidepanel
  addOptionsModalOpen: boolean;
  addSidepanelOpen: boolean;
  setAddOptionsModalOpen: (open: boolean) => void;
  setAddSidepanelOpen: (open: boolean) => void;

  // Export
  exportDialogOpen: boolean;
  selectedIdsForExport: string[];
  openExportDialog: (ids: string[]) => void;
  closeExportDialog: () => void;

  // #446 — this store is a module singleton, so panel state outlives the React
  // tree. Without this the recipients page re-mounts with `sidepanelOpen` still
  // true and re-opens the previous recipient's panel on navigating back.
  resetPanels: () => void;
}

/**
 * Every transient panel/dialog field, in one place so the store's initial state
 * and `resetPanels()` can never drift apart.
 */
const INITIAL_PANEL_STATE = {
  // Sidepanel (view details)
  selectedRecipient: null,
  sidepanelOpen: false,
  // Edit sidepanel
  editRecipient: null,
  editSidepanelOpen: false,
  // Add modal/sidepanel
  addOptionsModalOpen: false,
  addSidepanelOpen: false,
  // Export
  exportDialogOpen: false,
  selectedIdsForExport: [],
} satisfies Partial<RecipientsUIState>;

export const useRecipientsUIStore = create<RecipientsUIState>()(
  devtools(
    (set) => ({
      ...INITIAL_PANEL_STATE,
      openSidepanel: (recipient) => set({ selectedRecipient: recipient, sidepanelOpen: true }),
      closeSidepanel: () => set({ selectedRecipient: null, sidepanelOpen: false }),
      updateSelectedRecipient: (updates) =>
        set((state) => ({
          selectedRecipient: state.selectedRecipient
            ? ({ ...state.selectedRecipient, ...updates } as Recipient)
            : null,
        })),

      openEditSidepanel: (recipient) => set({ editRecipient: recipient, editSidepanelOpen: true }),
      closeEditSidepanel: () => set({ editRecipient: null, editSidepanelOpen: false }),

      setAddOptionsModalOpen: (open) => set({ addOptionsModalOpen: open }),
      setAddSidepanelOpen: (open) => set({ addSidepanelOpen: open }),

      openExportDialog: (ids) => set({ exportDialogOpen: true, selectedIdsForExport: ids }),
      closeExportDialog: () => set({ exportDialogOpen: false, selectedIdsForExport: [] }),

      resetPanels: () => set({ ...INITIAL_PANEL_STATE }),
    }),
    { name: 'RecipientsUIStore' },
  ),
);
